import { SketchLineObject, SketchArcObject, SketchEllipseObject, SketchSplineObject, SketchParabolaObject, SketchStyleSplineObject } from '../../mesh';
import { Vector2, Vector3 } from 'three';
import { isAngleInArcRange, solveQuadratic, solveCubic, solveQuartic } from '../../utils/sketch/SketchCurveMathUtils';
import type { OffsetCandidate } from './type';


/**
 * 草图实体求交的候选交点（SketchEntityIntersect ①求解接口 compute*Intersections 的返回单元）。
 * parameter 为交点在第一实体上的位置参数，语义随第一实体类型：
 * 直线 = 线段参数 t∈[0,1]；样条 = 沿曲线累计弧长；圆弧 = 相对圆心极角；
 * 椭圆弧 = 椭圆参数角；抛物线 = 抛物线参数。
 */
export interface SketchIntersectionCandidate {
    /** 交点在第一实体上的位置参数（语义见类注释）。 */
    parameter: number;
    /** 交点世界坐标。 */
    worldPoint: Vector3;
}


/**
 * 草图实体求交（①求解层）—— SketchExtensionAndIntersectionProcessing 的求交部分拆分。
 * 唯一功能: 对多段直线/曲线/圆弧/椭圆弧/样条/抛物线的组合图形, 求两实体
 *  （可沿各自延申规则延长后）的全部交点（compute*Intersections），返回全部候选交点，
 *  供 {@link SketchEntityNewControlPoint}（②选取层）按延伸方向选取离延伸端最近者
 *  并包装为交点控制点，完成"延申-求交-截取"流程。本类只负责求交，不承担延伸与选取。
 *
 * 求交矩阵:
 *  Line/Arc/Ellipse/Spline/Parabola 两两组合（共 15 类组合 + 逆序包装）。
 */
export class SketchEntityIntersect {



    //////////////////////////////////////////////////// 计算相交点
    /**
     *  left \ right	Line	       Arc	                        Ellipse	            Spline          Parabola
        Line	        线-线（1 次）	线-圆（2 次，最多 2 交点）	 线-椭圆（2 次）	     线-多段折线     线-抛物线
        Arc	            圆-线（同上）	圆-圆（2 次）	            圆-椭圆（4 次）	        圆-多段折线     圆-抛物线
        Ellipse	        椭圆-线        椭圆-圆	                   椭圆-椭圆（4 次）	   椭圆-多段折线    椭圆-抛物线
        Spline	        折线-线             折线-圆	                   折线-椭圆	           Bezier 递归求交 折线-抛物线
        Parabola        抛物线-线      抛物线-圆                   抛物线-椭圆             抛物线-多段折线  抛物线-抛物线
     * 
     */

    /**
     * 求两条线段的交点（仅求交，不含截取）。
     * 
     * 实现说明：
     * 1. 在参考面局部 2D 坐标系中用克莱姆法则求解两条线段所在直线的交点；
     * 2. 交点控制点使用独立实例返回，由调用方决定如何截取/消费；
     * 3. 两条线段所在直线平行时返回空数组（无交点）。
     * 
     * @param line1 第一条线段。
     * @param line2 第二条线段。
     * @returns 交点控制点数组（至多 1 个交点；平行/无交返回空数组）。
     */

    /**
     * 求线段与线段的全部交点（仅求交，不含截取，不含延伸）。
     * 说明：本函数只对传入的两条线段求交，交点必须同时落在两条线段的可见区间内——
     * 先求两线段所在直线的交点，再按线段 1 参数 t 与线段 2 参数 s 做双重可见区间过滤
     * （容差内允许端点相接，与 {@link computeLineArcIntersections} 的过滤容差一致），
     * 线-线至多 1 个交点，天然无重复无需去重；
     * 每个候选记录交点在第一实体上的位置参数（parameter）与世界坐标（worldPoint）。
     * 需要"延长线交点"的场景（如等距实体延伸求交）由调用方先经
     * {@link SketchEntityExtension} 延长线段后再传入本函数，本函数不承担延伸职责。
     * @param line1 第一条线段。
     * @param line2 第二条线段。
     * @returns 全部候选交点数组（无交返回空数组）。
     */
    static computeLineLineIntersections(line1: SketchLineObject, line2: SketchLineObject): SketchIntersectionCandidate[] {
        const referencePlane = line1.referencePlane;

        // 1. 读取四个端点世界坐标并投影到参考面 2D 局部坐标
        const a1World = line1.startPoint.position.clone();
        const b1World = line1.endPoint.position.clone();
        const a2World = line2.startPoint.position.clone();
        const b2World = line2.endPoint.position.clone();

        const a1 = referencePlane.worldToLocal(a1World);
        const b1 = referencePlane.worldToLocal(b1World);
        const a2 = referencePlane.worldToLocal(a2World);
        const b2 = referencePlane.worldToLocal(b2World);

        // 2. 计算两条线段所在直线的交点（2D 克莱姆法则）
        const d1x = b1.x - a1.x;
        const d1y = b1.y - a1.y;
        const d2x = b2.x - a2.x;
        const d2y = b2.y - a2.y;

        const denom = d1x * d2y - d1y * d2x;

        // 两条直线平行：无交点
        if (Math.abs(denom) < 1e-12) {
            return [];
        }

        const dx = a2.x - a1.x;
        const dy = a2.y - a1.y;

        // 参数 t 在线段 1 上的位置
        const t = (dx * d2y - dy * d2x) / denom;

        // 参数 s 在线段 2 上的位置（对称克莱姆公式，避免除法回代引入精度损失）
        const s = (dx * d1y - dy * d1x) / denom;

        // 3. 可见区间过滤：交点必须同时位于两条线段上；容差内端点相接视为有效
        //    （与 computeLineArcIntersections 的 t∈[-1e-10, 1+1e-10] 过滤保持一致）
        if (t < -1e-10 || t > 1 + 1e-10 || s < -1e-10 || s > 1 + 1e-10) {
            return [];
        }

        // 端点相接场景下将参数收敛到 [0,1]，保证 parameter 语义严格落在线段区间内
        const clampedT = Math.max(0, Math.min(1, t));

        // 交点局部 2D 坐标
        const intersectLocal = new Vector2(
            a1.x + clampedT * d1x,
            a1.y + clampedT * d1y,
        );

        // 4. 转换交点回世界 3D 坐标
        const intersectWorld = referencePlane.localToWorld(intersectLocal);

        // 5. 返回候选交点（线段-线段至多 1 个交点；parameter = 交点在线段 1 上的参数）
        return [{ parameter: clampedT, worldPoint: intersectWorld.clone() }];

    }

    /**
     * 求直线与圆弧的全部交点（仅求交，不含截取）。
     * 说明：计算两实体的全部候选交点（含各自可见区间过滤与去重），每个候选记录
     * 交点在第一实体上的位置参数（parameter）与世界坐标（worldPoint）；
     * "从全部交点中选取离延伸端最近者"由对应 intersect 方法完成。
     * @param line
     * @param arc
     * @returns 全部候选交点数组（无交返回空数组）。
     */
    static computeLineArcIntersections(line: SketchLineObject, arc: SketchArcObject): SketchIntersectionCandidate[] {
        // ═══════════════════════════════════════════════════════════
        // 1. 求解线段和圆弧的交点
        // ═══════════════════════════════════════════════════════════

        const referencePlane = line.referencePlane;

        // 1.1 获取直线的两个端点并投影到参考面局部 2D 坐标
        const lineStartWorld = line.startPoint.position.clone();
        const lineEndWorld = line.endPoint.position.clone();
        const lineStart = referencePlane.worldToLocal(lineStartWorld);
        const lineEnd = referencePlane.worldToLocal(lineEndWorld);

        // 1.2 获取圆弧定义，圆心投影到局部 2D
        const arcDef = arc.getArcDefinition();
        const centerLocal = referencePlane.worldToLocal(arcDef.centerWorld.clone());
        const { radius, startAngle, endAngle, clockwise } = arcDef;

        // 有效交点列表：保存直线参数 t 与对应世界坐标（Step 2 用于判定第一个交点）
        const validIntersections: SketchIntersectionCandidate[] = [];

        // 1.3 计算直线方向向量
        const dx = lineEnd.x - lineStart.x;
        const dy = lineEnd.y - lineStart.y;
        const lenSq = dx * dx + dy * dy;

        // 1.4 退化处理：线段退化为点（长度 < 1e-12）
        if (lenSq < 1e-12) {
            const distToCenter = Math.sqrt(
                (lineStart.x - centerLocal.x) ** 2 + (lineStart.y - centerLocal.y) ** 2,
            );
            if (Math.abs(distToCenter - radius) < 1e-8) {
                const angle = Math.atan2(lineStart.y - centerLocal.y, lineStart.x - centerLocal.x);
                if (isAngleInArcRange(
                    angle, startAngle, endAngle, clockwise,
                )) {
                    // 退化点即交点（起点即终点即交点），交点即线段自身位置
                    validIntersections.push({
                        parameter: 0,
                        worldPoint: lineStartWorld.clone(),
                    });
                }
            }
        } else {
            // 1.5 建立一元二次方程：|A + t·d - C|² = R²
            //     展开得 a·t² + b·t + c = 0，其中：
            //       a = d·d                      (方向向量点积)
            //       b = 2·(f·d)                  (f = A - C)
            //       c = f·f - R²
            const fx = lineStart.x - centerLocal.x;
            const fy = lineStart.y - centerLocal.y;

            const a = lenSq;                               // d·d
            const b = 2 * (fx * dx + fy * dy);             // 2·(f·d)
            const c = fx * fx + fy * fy - radius * radius; // f·f - R²

            const discriminant = b * b - 4 * a * c;
            // 判别式量级参考（两项绝对值之和）：小系数几何（坐标 0.01~0.1 量级）下
            // 判别式绝对值可能远小于固定容差但符号明确，绝对容差（1e-10）会把两个
            // 不同实根误合并为重根导致漏交点，故改为相对容差判定（与 solveQuadratic 一致）
            const discriminantScale = b * b + Math.abs(4 * a * c);
            const discriminantTolerance = discriminantScale * 1e-12;

            // 判别式 < -容差 → 无交点（直线与圆不相交）
            if (discriminant >= -discriminantTolerance) {
                const sqrtDisc = Math.sqrt(Math.max(0, discriminant));

                // 收集所有根（相切时只有 1 个，否则 2 个）
                const roots: number[] = [];
                if (discriminant <= discriminantTolerance) {
                    roots.push(-b / (2 * a));
                } else {
                    roots.push((-b - sqrtDisc) / (2 * a));
                    roots.push((-b + sqrtDisc) / (2 * a));
                }

                // 1.6 验证每个根：t ∈ [0,1] 且角度在圆弧范围内
                for (const t of roots) {
                    if (t < -1e-10 || t > 1 + 1e-10) continue;

                    const clampedT = Math.max(0, Math.min(1, t));

                    const ix = lineStart.x + clampedT * dx;
                    const iy = lineStart.y + clampedT * dy;

                    const angle = Math.atan2(iy - centerLocal.y, ix - centerLocal.x);

                    if (!isAngleInArcRange(
                        angle, startAngle, endAngle, clockwise,
                    )) continue;

                    validIntersections.push({
                        parameter: clampedT,
                        worldPoint: referencePlane.localToWorld(new Vector2(ix, iy)),
                    });
                }
            }
        }

        return validIntersections;
    }

    /**
     * 求直线与椭圆弧的全部交点（仅求交，不含截取）。
     * 说明：计算两实体的全部候选交点（含各自可见区间过滤与去重），每个候选记录
     * 交点在第一实体上的位置参数（parameter）与世界坐标（worldPoint）；
     * "从全部交点中选取离延伸端最近者"由对应 intersect 方法完成。
     * @param line
     * @param ellipse
     * @returns 全部候选交点数组（无交返回空数组）。
     */
    static computeLineEllipseIntersections(line: SketchLineObject, ellipse: SketchEllipseObject): SketchIntersectionCandidate[] {
        const referencePlane = line.referencePlane;

        // 1. 获取直线端点与椭圆定义，统一投影到参考面局部 2D 坐标
        const lineStartWorld = line.startPoint.position.clone();
        const lineEndWorld = line.endPoint.position.clone();
        const lineStart = referencePlane.worldToLocal(lineStartWorld);
        const lineEnd = referencePlane.worldToLocal(lineEndWorld);

        const ellipseDef = ellipse.getEllipseDefinition();
        const centerLocal = referencePlane.worldToLocal(ellipseDef.centerWorld.clone());
        const { radiusX, radiusY, rotation, startAngle, endAngle, clockwise } = ellipseDef;

        // 有效交点列表：保存直线参数 λ 与对应世界坐标（Step 6 用于判定第一个交点）
        const validIntersections: SketchIntersectionCandidate[] = [];

        // 退化椭圆保护：任一半轴非正时不存在有效椭圆，直接返回
        if (radiusX <= 1e-12 || radiusY <= 1e-12) {
            return [];
        }

        // 预计算旋转角正余弦，供主轴系坐标变换复用
        const cosRot = Math.cos(rotation);
        const sinRot = Math.sin(rotation);

        // 2. 计算直线方向向量
        const dx = lineEnd.x - lineStart.x;
        const dy = lineEnd.y - lineStart.y;
        const lenSq = dx * dx + dy * dy;

        // 3. 退化处理：线段退化为点（长度 < 1e-12）
        if (lenSq < 1e-12) {
            // 将点变换到椭圆主轴系，代入隐式方程判定是否在椭圆上
            const u = lineStart.x - centerLocal.x;
            const v = lineStart.y - centerLocal.y;
            const xPrime = u * cosRot + v * sinRot;
            const yPrime = -u * sinRot + v * cosRot;
            const implicit = (xPrime * xPrime) / (radiusX * radiusX)
                + (yPrime * yPrime) / (radiusY * radiusY);

            if (Math.abs(implicit - 1) < 1e-8) {
                const angle = Math.atan2(yPrime / radiusY, xPrime / radiusX);
                if (isAngleInArcRange(
                    angle, startAngle, endAngle, clockwise,
                )) {
                    // 退化点即交点（起点即终点即交点），交点即线段自身位置
                    validIntersections.push({
                        parameter: 0,
                        worldPoint: lineStartWorld.clone(),
                    });
                }
            }
        } else {
            // 4. 建立一元二次方程：将直线 P = A + λ·d 代入旋转椭圆隐式方程
            //    主轴系下 x'(λ) = a1·λ + b1，y'(λ) = a2·λ + b2，其中：
            //      a1 = dx·cosRot + dy·sinRot，b1 = u0·cosRot + v0·sinRot
            //      a2 = -dx·sinRot + dy·cosRot，b2 = -u0·sinRot + v0·cosRot
            //    展开 (a1·λ+b1)²/rx² + (a2·λ+b2)²/ry² = 1 得 a·λ² + b·λ + c = 0
            const u0 = lineStart.x - centerLocal.x;
            const v0 = lineStart.y - centerLocal.y;

            // x' 的 λ 系数与常数项
            const a1 = dx * cosRot + dy * sinRot;
            const b1 = u0 * cosRot + v0 * sinRot;
            // y' 的 λ 系数与常数项
            const a2 = -dx * sinRot + dy * cosRot;
            const b2 = -u0 * sinRot + v0 * cosRot;

            const a = (a1 * a1) / (radiusX * radiusX) + (a2 * a2) / (radiusY * radiusY);
            const b = 2 * ((a1 * b1) / (radiusX * radiusX) + (a2 * b2) / (radiusY * radiusY));
            const c = (b1 * b1) / (radiusX * radiusX) + (b2 * b2) / (radiusY * radiusY) - 1;

            const discriminant = b * b - 4 * a * c;
            // 判别式量级参考（两项绝对值之和）：小系数几何（坐标 0.01~0.1 量级）下
            // 判别式绝对值可能远小于固定容差但符号明确，绝对容差（1e-10）会把两个
            // 不同实根误合并为重根导致漏交点，故改为相对容差判定（与 solveQuadratic 一致）
            const discriminantScale = b * b + Math.abs(4 * a * c);
            const discriminantTolerance = discriminantScale * 1e-12;

            // 判别式 < -容差 → 无交点（直线与椭圆不相交）
            if (discriminant >= -discriminantTolerance) {
                const sqrtDisc = Math.sqrt(Math.max(0, discriminant));

                // 收集所有根（相切时只有 1 个，否则 2 个）
                const roots: number[] = [];
                if (discriminant <= discriminantTolerance) {
                    roots.push(-b / (2 * a));
                } else {
                    roots.push((-b - sqrtDisc) / (2 * a));
                    roots.push((-b + sqrtDisc) / (2 * a));
                }

                // 5. 验证每个根：λ ∈ [0,1] 且椭圆参数角在弧区间内
                for (const lambda of roots) {
                    if (lambda < -1e-10 || lambda > 1 + 1e-10) continue;

                    const clampedLambda = Math.max(0, Math.min(1, lambda));

                    const ix = lineStart.x + clampedLambda * dx;
                    const iy = lineStart.y + clampedLambda * dy;

                    // 变换到椭圆主轴系并反解参数角
                    const u = ix - centerLocal.x;
                    const v = iy - centerLocal.y;
                    const xPrime = u * cosRot + v * sinRot;
                    const yPrime = -u * sinRot + v * cosRot;
                    const angle = Math.atan2(yPrime / radiusY, xPrime / radiusX);

                    if (!isAngleInArcRange(
                        angle, startAngle, endAngle, clockwise,
                    )) continue;

                    validIntersections.push({
                        parameter: clampedLambda,
                        worldPoint: referencePlane.localToWorld(new Vector2(ix, iy)),
                    });
                }
            }
        }

        return validIntersections;
    }

    /**
     * 求直线与样条曲线的全部交点（仅求交，不含截取）。
     * 说明：计算两实体的全部候选交点（含各自可见区间过滤与去重），每个候选记录
     * 交点在第一实体上的位置参数（parameter）与世界坐标（worldPoint）；
     * "从全部交点中选取离延伸端最近者"由对应 intersect 方法完成。
     * @param line
     * @param spline
     * @returns 全部候选交点数组（无交返回空数组）。
     */
    static computeLineSplineIntersections(line: SketchLineObject, spline: SketchSplineObject): SketchIntersectionCandidate[] {
        const referencePlane = line.referencePlane;

        // 1. 获取直线端点并投影到参考面局部 2D 坐标
        const lineStartWorld = line.startPoint.position.clone();
        const lineEndWorld = line.endPoint.position.clone();
        const lineStart = referencePlane.worldToLocal(lineStartWorld);
        const lineEnd = referencePlane.worldToLocal(lineEndWorld);

        // 直线方向向量与长度平方
        const d1x = lineEnd.x - lineStart.x;
        const d1y = lineEnd.y - lineStart.y;
        const lenSq = d1x * d1x + d1y * d1y;

        // 直线退化为点：无方向可求交
        if (lenSq < 1e-12) {
            return [];
        }

        // 2. 获取样条曲线采样点，构建待检查的段序列
        const curvePoints = spline.getCurvePoints();
        if (curvePoints.length < 2) {
            return [];
        }

        // 段序列：相邻采样点对；闭合样条补上首尾接缝段
        const segmentPairs: Array<[Vector3, Vector3]> = [];
        for (let i = 0; i < curvePoints.length - 1; i++) {
            segmentPairs.push([curvePoints[i]!, curvePoints[i + 1]!]);
        }
        const metadata = spline.getCurveMetadata();
        if (metadata.closureKind === 'closed') {
            segmentPairs.push([curvePoints[curvePoints.length - 1]!, curvePoints[0]!]);
        }

        // 3. 逐段求交，记录交点沿样条的累计弧长参数
        //    有效交点列表：{ length: 弧长参数, worldPoint: 世界坐标 }
        const validIntersections: SketchIntersectionCandidate[] = [];
        // 当前段起点的累计弧长
        let accumulatedLength = 0;

        for (const [segStart, segEnd] of segmentPairs) {
            // 段端点投影到参考面局部 2D 坐标
            const a2 = referencePlane.worldToLocal(segStart);
            const b2 = referencePlane.worldToLocal(segEnd);

            const d2x = b2.x - a2.x;
            const d2y = b2.y - a2.y;
            const segLen = Math.sqrt(d2x * d2x + d2y * d2y);

            // 退化段（长度 < 1e-12）：跳过，仅累计弧长
            if (segLen >= 1e-12) {
                // 两条线段所在直线平行：该段与直线无交点
                const denom = d1x * d2y - d1y * d2x;
                if (Math.abs(denom) < 1e-12) {
                    accumulatedLength += segLen;
                    continue;
                }

                const dx = a2.x - lineStart.x;
                const dy = a2.y - lineStart.y;

                // 直线参数 t 与样条段参数 s（2D 克莱姆法则）
                const t = (dx * d2y - dy * d2x) / denom;
                const s = (dx * d1y - dy * d1x) / denom;

                // 交点必须同时落在直线段与样条段范围内
                if (t < -1e-10 || t > 1 + 1e-10) {
                    accumulatedLength += segLen;
                    continue;
                }
                if (s < -1e-10 || s > 1 + 1e-10) {
                    accumulatedLength += segLen;
                    continue;
                }

                // 交点坐标按直线参数 t 计算，弧长定位按样条段参数 s 计算
                const clampedT = Math.max(0, Math.min(1, t));
                const clampedS = Math.max(0, Math.min(1, s));
                const ix = lineStart.x + clampedT * d1x;
                const iy = lineStart.y + clampedT * d1y;

                validIntersections.push({
                    parameter: accumulatedLength + clampedS * segLen,
                    worldPoint: referencePlane.localToWorld(new Vector2(ix, iy)),
                });
            }

            accumulatedLength += segLen;
        }

        // 去重：相邻段共享采样点会产生重复交点（同一位置），仅保留一个
        const deduplicated: SketchIntersectionCandidate[] = [];
        for (const item of validIntersections) {
            const isDuplicate = deduplicated.some(
                (existing) => existing.worldPoint.distanceToSquared(item.worldPoint) < 1e-16,
            );
            if (!isDuplicate) {
                deduplicated.push(item);
            }
        }
        return deduplicated;
    }

    /**
     * 求直线与抛物线的全部交点（仅求交，不含截取）。
     * 说明：计算两实体的全部候选交点（含各自可见区间过滤与去重），每个候选记录
     * 交点在第一实体上的位置参数（parameter）与世界坐标（worldPoint）；
     * "从全部交点中选取离延伸端最近者"由对应 intersect 方法完成。
     * @param line
     * @param parabola
     * @returns 全部候选交点数组（无交返回空数组）。
     */
    static computeLineParabolaIntersections(line: SketchLineObject, parabola: SketchParabolaObject): SketchIntersectionCandidate[] {
        const referencePlane = line.referencePlane;

        // 1. 获取直线端点并投影到参考面局部 2D 坐标
        const lineStartWorld = line.startPoint.position.clone();
        const lineEndWorld = line.endPoint.position.clone();
        const lineStart = referencePlane.worldToLocal(lineStartWorld);
        const lineEnd = referencePlane.worldToLocal(lineEndWorld);

        // 直线方向向量与长度平方
        const dx = lineEnd.x - lineStart.x;
        const dy = lineEnd.y - lineStart.y;
        const lenSq = dx * dx + dy * dy;

        // 2. 由抛物线定义推导局部框架（复刻 SketchParabolaObject.resolveFrameFromDefinition）
        const definition = parabola.getParabolaDefinition();
        const apexWorld = definition.apexWorld.clone();
        const focalWorld = definition.focalWorld.clone();
        const { startParameter, endParameter } = definition;

        // 参数区间边界（兼容端点反向越过：按最小/最大归一）
        const minParam = Math.min(startParameter, endParameter);
        const maxParam = Math.max(startParameter, endParameter);

        // 顶点指向焦点的轴向（去掉参考面法向分量，保证落在参考面内）
        const normal = referencePlane.normal.clone().normalize();
        const axisVector = focalWorld.clone().sub(apexWorld);
        axisVector.sub(normal.clone().multiplyScalar(axisVector.dot(normal)));
        const focalLength = axisVector.length();

        // 焦点与顶点重合（投影退化）：抛物线定义无效，无交点
        if (focalLength < 1e-12) {
            return [];
        }

        const axisDirWorld = axisVector.normalize();
        const sideDirWorld = new Vector3().crossVectors(axisDirWorld, normal).normalize();

        // 将轴向/侧向轴投影到参考面局部 2D（点差法，与 SketchParabolaObject 内部一致）
        const apexLocal = referencePlane.worldToLocal(apexWorld);
        const axis2D = referencePlane.worldToLocal(apexWorld.clone().add(axisDirWorld)).sub(apexLocal);
        const side2D = referencePlane.worldToLocal(apexWorld.clone().add(sideDirWorld)).sub(apexLocal);
        const axd = axis2D.x;
        const ayd = axis2D.y;
        const sxd = side2D.x;
        const syd = side2D.y;

        // 有效交点列表：保存直线参数 λ 与对应世界坐标（Step 6 用于判定第一个交点）
        const validIntersections: SketchIntersectionCandidate[] = [];

        // 3. 退化处理：线段退化为点（长度 < 1e-12）
        if (lenSq < 1e-12) {
            // 将点投影到侧向轴得参数 t，反查曲线点并比较距离判定是否在抛物线上
            const tProj = (lineStart.x - apexLocal.x) * sxd + (lineStart.y - apexLocal.y) * syd;
            const curveLocal = new Vector2(
                apexLocal.x + sxd * tProj + axd * (tProj * tProj) / (4 * focalLength),
                apexLocal.y + syd * tProj + ayd * (tProj * tProj) / (4 * focalLength),
            );
            const distSq = (lineStart.x - curveLocal.x) ** 2 + (lineStart.y - curveLocal.y) ** 2;

            if (distSq < 1e-16 && tProj >= minParam - 1e-10 && tProj <= maxParam + 1e-10) {
                // 退化点即交点（起点即终点即交点），交点即线段自身位置
                validIntersections.push({
                    parameter: 0,
                    worldPoint: lineStartWorld.clone(),
                });
            }
        } else {
            // 4. 建立一元二次方程：将 P(t) 代入直线共线条件 cross(P(t) - A, d) = 0
            //    (ax + sxd·t + axd·t²/(4f) - lx)·dy - (ay + syd·t + ayd·t²/(4f) - ly)·dx = 0
            //    展开得 A·t² + B·t + C = 0，其中：
            //      A = (axd·dy - ayd·dx) / (4f)
            //      B = sxd·dy - syd·dx
            //      C = (ax - lx)·dy - (ay - ly)·dx
            const A = (axd * dy - ayd * dx) / (4 * focalLength);
            const B = sxd * dy - syd * dx;
            const C = (apexLocal.x - lineStart.x) * dy - (apexLocal.y - lineStart.y) * dx;

            // 收集候选参数根
            const roots: number[] = [];
            if (Math.abs(A) < 1e-12) {
                // 退化：直线与抛物线轴向平行，退化为一次方程 B·t + C = 0
                if (Math.abs(B) < 1e-12) {
                    return []; // 直线与抛物线渐近方向平行且不共线，无交点
                }
                roots.push(-C / B);
            } else {
                const discriminant = B * B - 4 * A * C;
                // 判别式量级参考（两项绝对值之和）：小系数几何（坐标 0.01~0.1 量级）下
                // 判别式绝对值可能远小于固定容差但符号明确，绝对容差（1e-10）会把两个
                // 不同实根误合并为重根导致漏交点，故改为相对容差判定（与 solveQuadratic 一致）
                const discriminantScale = B * B + Math.abs(4 * A * C);
                const discriminantTolerance = discriminantScale * 1e-12;

                // 判别式 < -容差 → 无实根（直线与抛物线不相交）
                if (discriminant < -discriminantTolerance) {
                    return [];
                }
                const sqrtDisc = Math.sqrt(Math.max(0, discriminant));

                if (discriminant <= discriminantTolerance) {
                    roots.push(-B / (2 * A));
                } else {
                    roots.push((-B - sqrtDisc) / (2 * A));
                    roots.push((-B + sqrtDisc) / (2 * A));
                }
            }

            // 5. 验证每个根：t 在参数区间内且直线参数 λ ∈ [0,1]
            for (const t of roots) {
                if (t < minParam - 1e-10 || t > maxParam + 1e-10) continue;

                // 抛物线上的精确交点（局部 2D）
                const curveLocal = new Vector2(
                    apexLocal.x + sxd * t + axd * (t * t) / (4 * focalLength),
                    apexLocal.y + syd * t + ayd * (t * t) / (4 * focalLength),
                );

                // 直线参数 λ = ((P - A)·d) / |d|²，需落在 [0,1] 内
                const lambda = ((curveLocal.x - lineStart.x) * dx + (curveLocal.y - lineStart.y) * dy) / lenSq;
                if (lambda < -1e-10 || lambda > 1 + 1e-10) continue;

                validIntersections.push({
                    parameter: Math.max(0, Math.min(1, lambda)),
                    worldPoint: referencePlane.localToWorld(curveLocal),
                });
            }
        }

        return validIntersections;
    }

    /**
     * 求圆弧与圆弧的全部交点（仅求交，不含截取）。
     * 说明：计算两实体的全部候选交点（含各自可见区间过滤与去重），每个候选记录
     * 交点在第一实体上的位置参数（parameter）与世界坐标（worldPoint）；
     * "从全部交点中选取离延伸端最近者"由对应 intersect 方法完成。
     * @param arc1
     * @param arc2
     * @returns 全部候选交点数组（无交返回空数组）。
     */
    static computeArcArcIntersections(arc1: SketchArcObject, arc2: SketchArcObject): SketchIntersectionCandidate[] {
        const referencePlane = arc1.referencePlane;

        // 1. 获取两圆弧定义，圆心投影到参考面局部 2D 坐标
        const arc1Def = arc1.getArcDefinition();
        const arc2Def = arc2.getArcDefinition();
        const c1 = referencePlane.worldToLocal(arc1Def.centerWorld.clone());
        const c2 = referencePlane.worldToLocal(arc2Def.centerWorld.clone());
        const r1 = arc1Def.radius;
        const r2 = arc2Def.radius;
        const { startAngle: start1, endAngle: end1, clockwise: clockwise1 } = arc1Def;
        const { startAngle: start2, endAngle: end2, clockwise: clockwise2 } = arc2Def;

        // 半径无效防御
        if (r1 <= 1e-12 || r2 <= 1e-12) {
            return [];
        }

        // 2. 两满圆求交
        const dx = c2.x - c1.x;
        const dy = c2.y - c1.y;
        const distSq = dx * dx + dy * dy;
        const dist = Math.sqrt(distSq);

        // 同心圆：半径相同则重合（无数交点），不同则内含/外离，均无唯一交点
        if (dist < 1e-12) {
            return [];
        }

        // 外离或内含：无交点
        if (dist > r1 + r2 + 1e-10 || dist < Math.abs(r1 - r2) - 1e-10) {
            return [];
        }

        // 交点垂足到圆 1 圆心的距离 a（余弦定理），以及半弦长 h（相切时 h ≈ 0）
        const a = (r1 * r1 - r2 * r2 + distSq) / (2 * dist);
        const hSq = r1 * r1 - a * a;
        if (hSq < -1e-8) {
            return []; // 数值防御：数学上应已无交点
        }
        const h = hSq > 0 ? Math.sqrt(hSq) : 0;

        // 连心线单位方向与垂足
        const unitX = dx / dist;
        const unitY = dy / dist;
        const footX = c1.x + a * unitX;
        const footY = c1.y + a * unitY;

        // 候选交点局部 2D 列表（垂直于连心线方向偏移 ±h）
        const candidateLocalPoints: Vector2[] = [];
        if (h <= 1e-10) {
            // 相切：单个交点
            candidateLocalPoints.push(new Vector2(footX, footY));
        } else {
            candidateLocalPoints.push(new Vector2(footX - h * unitY, footY + h * unitX));
            candidateLocalPoints.push(new Vector2(footX + h * unitY, footY - h * unitX));
        }

        // 3. 过滤：交点极角须同时落在两圆弧的角度区间内
        //    保存交点相对 arc1 圆心的极角 angle1（弧上参数，Step 4 用于判定第一个交点）
        const validIntersections: SketchIntersectionCandidate[] = [];
        for (const localPoint of candidateLocalPoints) {
            const angle1 = Math.atan2(localPoint.y - c1.y, localPoint.x - c1.x);
            const angle2 = Math.atan2(localPoint.y - c2.y, localPoint.x - c2.x);

            if (!isAngleInArcRange(
                angle1, start1, end1, clockwise1,
            )) continue;
            if (!isAngleInArcRange(
                angle2, start2, end2, clockwise2,
            )) continue;

            validIntersections.push({
                parameter: angle1,
                worldPoint: referencePlane.localToWorld(localPoint),
            });
        }

        return validIntersections;
    }

    /**
     * 求圆弧与椭圆弧的全部交点（仅求交，不含截取）。
     * 说明：计算两实体的全部候选交点（含各自可见区间过滤与去重），每个候选记录
     * 交点在第一实体上的位置参数（parameter）与世界坐标（worldPoint）；
     * "从全部交点中选取离延伸端最近者"由对应 intersect 方法完成。
     * @param arc
     * @param ellipse
     * @returns 全部候选交点数组（无交返回空数组）。
     */
    static computeArcEllipseIntersections(arc: SketchArcObject, ellipse: SketchEllipseObject): SketchIntersectionCandidate[] {
        const referencePlane = arc.referencePlane;

        // 1. 获取两曲线定义，圆心/椭圆中心投影到参考面局部 2D 坐标
        const arcDef = arc.getArcDefinition();
        const ellipseDef = ellipse.getEllipseDefinition();
        const centerLocal = referencePlane.worldToLocal(arcDef.centerWorld.clone());
        const ellipseCenterLocal = referencePlane.worldToLocal(ellipseDef.centerWorld.clone());
        const radius = arcDef.radius;
        const { radiusX, radiusY, rotation } = ellipseDef;
        const { startAngle: arcStart, endAngle: arcEnd, clockwise: arcClockwise } = arcDef;
        const {
            startAngle: ellipseStart,
            endAngle: ellipseEnd,
            clockwise: ellipseClockwise,
        } = ellipseDef;

        // 半径/半轴有效性防御
        if (radius <= 1e-12 || radiusX <= 1e-12 || radiusY <= 1e-12) {
            return [];
        }

        // 预计算旋转角正余弦，供椭圆参数方程复用
        const cr = Math.cos(rotation);
        const sr = Math.sin(rotation);

        // 2. 建立圆-椭圆交点方程：|P(θ) - C|² = r² 展开
        //    ux = A0 + rx·cr·cosθ - ry·sr·sinθ，uy = B0 + rx·sr·cosθ + ry·cr·sinθ
        //    ux² + uy² 展开后 cosθ·sinθ 项抵消，得：
        //    rx²·cos²θ + ry²·sin²θ + D·cosθ + E·sinθ + F = 0
        const A0 = ellipseCenterLocal.x - centerLocal.x;
        const B0 = ellipseCenterLocal.y - centerLocal.y;
        const D = 2 * radiusX * (A0 * cr + B0 * sr);
        const E = 2 * radiusY * (-A0 * sr + B0 * cr);
        const F = A0 * A0 + B0 * B0 - radius * radius;

        // 3. 万能代换 t = tan(θ/2)：cosθ = (1-t²)/(1+t²)，sinθ = 2t/(1+t²)
        //    乘 (1+t²)² 后化为四次方程 a4·t⁴ + a3·t³ + a2·t² + a1·t + a0 = 0
        const a4 = radiusX * radiusX - D + F;
        const a3 = 2 * E;
        const a2 = -2 * radiusX * radiusX + 4 * radiusY * radiusY + 2 * F;
        const a1 = 2 * E;
        const a0 = radiusX * radiusX + D + F;

        const tRoots = solveQuartic(a4, a3, a2, a1, a0);

        // 候选椭圆参数角：θ = 2·atan(t) 覆盖 (-π, π)
        const candidateAngles: number[] = tRoots.map((t) => 2 * Math.atan(t));

        // 代换奇点检测：θ = π（t → ∞）处单独按方程残差判定
        // ux(π) = A0 - rx·cr，uy(π) = B0 - rx·sr（cosπ = -1，sinπ = 0）
        const uxPi = A0 - radiusX * cr;
        const uyPi = B0 - radiusX * sr;
        const residualPi = uxPi * uxPi + uyPi * uyPi - radius * radius;
        if (Math.abs(residualPi) < 1e-8) {
            candidateAngles.push(Math.PI);
        }

        // 4. 过滤与去重：椭圆参数角在椭圆弧区间内、相对圆心的极角在圆弧区间内
        //    保存交点相对 arc 圆心的极角 arcAngle（弧上参数，Step 5 用于判定第一个交点）
        const validIntersections: SketchIntersectionCandidate[] = [];
        for (const theta of candidateAngles) {
            // 椭圆参数角需落在椭圆弧可见区间内
            if (!isAngleInArcRange(
                theta, ellipseStart, ellipseEnd, ellipseClockwise,
            )) continue;

            // 交点局部 2D 坐标（用椭圆参数方程精确计算）
            const localPoint = new Vector2(
                ellipseCenterLocal.x + radiusX * cr * Math.cos(theta) - radiusY * sr * Math.sin(theta),
                ellipseCenterLocal.y + radiusX * sr * Math.cos(theta) + radiusY * cr * Math.sin(theta),
            );

            // 交点相对圆心的极角需落在圆弧可见区间内
            const arcAngle = Math.atan2(localPoint.y - centerLocal.y, localPoint.x - centerLocal.x);
            if (!isAngleInArcRange(
                arcAngle, arcStart, arcEnd, arcClockwise,
            )) continue;

            // 去重：相切重根或数值噪声产生的重复交点仅保留一个
            const worldPoint = referencePlane.localToWorld(localPoint);
            const isDuplicate = validIntersections.some(
                (existing) => existing.worldPoint.distanceToSquared(worldPoint) < 1e-16,
            );
            if (isDuplicate) continue;

            validIntersections.push({ parameter: arcAngle, worldPoint });
        }

        return validIntersections;
    }

    /**
     * 求圆弧与样条曲线的全部交点（仅求交，不含截取）。
     * 说明：计算两实体的全部候选交点（含各自可见区间过滤与去重），每个候选记录
     * 交点在第一实体上的位置参数（parameter）与世界坐标（worldPoint）；
     * "从全部交点中选取离延伸端最近者"由对应 intersect 方法完成。
     * @param arc
     * @param spline
     * @returns 全部候选交点数组（无交返回空数组）。
     */
    static computeArcSplineIntersections(arc: SketchArcObject, spline: SketchSplineObject): SketchIntersectionCandidate[] {
        const referencePlane = arc.referencePlane;

        // 1. 获取圆弧定义，圆心投影到参考面局部 2D 坐标
        const arcDef = arc.getArcDefinition();
        const centerLocal = referencePlane.worldToLocal(arcDef.centerWorld.clone());
        const { radius, startAngle, endAngle, clockwise } = arcDef;

        // 半径无效防御
        if (radius <= 1e-12) {
            return [];
        }

        // 2. 获取样条曲线采样点，构建待检查的段序列
        const curvePoints = spline.getCurvePoints();
        if (curvePoints.length < 2) {
            return [];
        }

        // 段序列：相邻采样点对；闭合样条补上首尾接缝段
        const segmentPairs: Array<[Vector3, Vector3]> = [];
        for (let i = 0; i < curvePoints.length - 1; i++) {
            segmentPairs.push([curvePoints[i]!, curvePoints[i + 1]!]);
        }
        const metadata = spline.getCurveMetadata();
        if (metadata.closureKind === 'closed') {
            segmentPairs.push([curvePoints[curvePoints.length - 1]!, curvePoints[0]!]);
        }

        // 3. 逐段与圆弧求交，记录交点沿样条的累计弧长参数
        //    有效交点列表：{ length: 弧长参数, worldPoint: 世界坐标 }
        const validIntersections: SketchIntersectionCandidate[] = [];
        // 当前段起点的累计弧长
        let accumulatedLength = 0;

        for (const [segStart, segEnd] of segmentPairs) {
            // 段端点投影到参考面局部 2D 坐标
            const a = referencePlane.worldToLocal(segStart);
            const b = referencePlane.worldToLocal(segEnd);

            const segDx = b.x - a.x;
            const segDy = b.y - a.y;
            const segLen = Math.sqrt(segDx * segDx + segDy * segDy);

            // 退化段（长度 < 1e-12）：跳过，仅累计弧长
            if (segLen >= 1e-12) {
                // 建立一元二次方程：|A + s·d - C|² = R²
                // 展开得 a·s² + b·s + c = 0，其中：
                //   a = d·d，b = 2·(f·d)，c = f·f - R²（f = A - C）
                const fx = a.x - centerLocal.x;
                const fy = a.y - centerLocal.y;

                const aq = segLen * segLen;                        // d·d
                const bq = 2 * (fx * segDx + fy * segDy);          // 2·(f·d)
                const cq = fx * fx + fy * fy - radius * radius;    // f·f - R²

                const discriminant = bq * bq - 4 * aq * cq;
                // 判别式量级参考（两项绝对值之和）：小系数几何（坐标 0.01~0.1 量级）下
                // 判别式绝对值可能远小于固定容差但符号明确，绝对容差（1e-10）会把两个
                // 不同实根误合并为重根导致漏交点，故改为相对容差判定（与 solveQuadratic 一致）
                const discriminantScale = bq * bq + Math.abs(4 * aq * cq);
                const discriminantTolerance = discriminantScale * 1e-12;

                // 判别式 < -容差 → 该段与圆不相交
                if (discriminant >= -discriminantTolerance) {
                    const sqrtDisc = Math.sqrt(Math.max(0, discriminant));

                    // 收集所有根（相切时只有 1 个，否则 2 个）
                    const roots: number[] = [];
                    if (discriminant <= discriminantTolerance) {
                        roots.push(-bq / (2 * aq));
                    } else {
                        roots.push((-bq - sqrtDisc) / (2 * aq));
                        roots.push((-bq + sqrtDisc) / (2 * aq));
                    }

                    // 验证每个根：s ∈ [0,1] 且角度在圆弧范围内
                    for (const s of roots) {
                        if (s < -1e-10 || s > 1 + 1e-10) continue;

                        const clampedS = Math.max(0, Math.min(1, s));

                        const ix = a.x + clampedS * segDx;
                        const iy = a.y + clampedS * segDy;

                        const angle = Math.atan2(iy - centerLocal.y, ix - centerLocal.x);

                        if (!isAngleInArcRange(
                            angle, startAngle, endAngle, clockwise,
                        )) continue;

                        validIntersections.push({
                            parameter: accumulatedLength + clampedS * segLen,
                            worldPoint: referencePlane.localToWorld(new Vector2(ix, iy)),
                        });
                    }
                }
            }

            accumulatedLength += segLen;
        }

        // 去重：相邻段共享采样点会产生重复交点（同一位置），仅保留一个
        const deduplicated: SketchIntersectionCandidate[] = [];
        for (const item of validIntersections) {
            const isDuplicate = deduplicated.some(
                (existing) => existing.worldPoint.distanceToSquared(item.worldPoint) < 1e-16,
            );
            if (!isDuplicate) {
                deduplicated.push(item);
            }
        }
        return deduplicated;
    }

    /**
     * 求圆弧与抛物线的全部交点（仅求交，不含截取）。
     * 说明：计算两实体的全部候选交点（含各自可见区间过滤与去重），每个候选记录
     * 交点在第一实体上的位置参数（parameter）与世界坐标（worldPoint）；
     * "从全部交点中选取离延伸端最近者"由对应 intersect 方法完成。
     * @param arc
     * @param parabola
     * @returns 全部候选交点数组（无交返回空数组）。
     */
    static computeArcParabolaIntersections(arc: SketchArcObject, parabola: SketchParabolaObject): SketchIntersectionCandidate[] {
        const referencePlane = arc.referencePlane;

        // 1. 获取圆弧定义，圆心投影到参考面局部 2D 坐标
        const arcDef = arc.getArcDefinition();
        const centerLocal = referencePlane.worldToLocal(arcDef.centerWorld.clone());
        const { radius, startAngle: arcStart, endAngle: arcEnd, clockwise: arcClockwise } = arcDef;

        // 半径无效防御
        if (radius <= 1e-12) {
            return [];
        }

        // 2. 由抛物线定义推导局部框架（复刻 SketchParabolaObject.resolveFrameFromDefinition）
        const definition = parabola.getParabolaDefinition();
        const apexWorld = definition.apexWorld.clone();
        const focalWorld = definition.focalWorld.clone();
        const { startParameter, endParameter } = definition;

        // 参数区间边界（兼容端点反向越过：按最小/最大归一）
        const minParam = Math.min(startParameter, endParameter);
        const maxParam = Math.max(startParameter, endParameter);

        // 顶点指向焦点的轴向（去掉参考面法向分量，保证落在参考面内）
        const normal = referencePlane.normal.clone().normalize();
        const axisVector = focalWorld.clone().sub(apexWorld);
        axisVector.sub(normal.clone().multiplyScalar(axisVector.dot(normal)));
        const focalLength = axisVector.length();

        // 焦点与顶点重合（投影退化）：抛物线定义无效，无交点
        if (focalLength < 1e-12) {
            return [];
        }

        const axisDirWorld = axisVector.normalize();
        const sideDirWorld = new Vector3().crossVectors(axisDirWorld, normal).normalize();

        // 将轴向/侧向轴投影到参考面局部 2D（点差法，与 SketchParabolaObject 内部一致）
        const apexLocal = referencePlane.worldToLocal(apexWorld);
        const axis2D = referencePlane.worldToLocal(apexWorld.clone().add(axisDirWorld)).sub(apexLocal);
        const side2D = referencePlane.worldToLocal(apexWorld.clone().add(sideDirWorld)).sub(apexLocal);

        // 3. 建立四次方程：|P(t) - C|² = r² 展开
        //    P(t) = apex + side·t + axis·t²/(4f)，|side| = |axis| = 1 且互相垂直
        //    展开后交叉项（t³ 与混合项）抵消，得：
        //      t⁴/(16f²) + [1 + (u·axis)/(2f)]·t² + 2(u·side)·t + (|u|² - r²) = 0
        //    其中 u = apex - C
        const ux = apexLocal.x - centerLocal.x;
        const uy = apexLocal.y - centerLocal.y;
        const uDotAxis = ux * axis2D.x + uy * axis2D.y;
        const uDotSide = ux * side2D.x + uy * side2D.y;
        const uLenSq = ux * ux + uy * uy;

        const a4 = 1 / (16 * focalLength * focalLength);
        const a3 = 0;
        const a2 = 1 + uDotAxis / (2 * focalLength);
        const a1 = 2 * uDotSide;
        const a0 = uLenSq - radius * radius;

        const tRoots = solveQuartic(a4, a3, a2, a1, a0);

        // 4. 过滤与去重：t 在抛物线参数区间内、交点相对圆心的极角在圆弧区间内
        //    保存交点的抛物线参数 t（位置参数，Step 5 用于判定第一个交点）
        const validIntersections: SketchIntersectionCandidate[] = [];
        for (const t of tRoots) {
            // 抛物线参数需落在可见参数区间内
            if (t < minParam - 1e-10 || t > maxParam + 1e-10) continue;

            // 抛物线上的交点（局部 2D）
            const localPoint = new Vector2(
                apexLocal.x + side2D.x * t + axis2D.x * (t * t) / (4 * focalLength),
                apexLocal.y + side2D.y * t + axis2D.y * (t * t) / (4 * focalLength),
            );

            // 交点相对圆心的极角需落在圆弧可见区间内
            const arcAngle = Math.atan2(localPoint.y - centerLocal.y, localPoint.x - centerLocal.x);
            if (!isAngleInArcRange(
                arcAngle, arcStart, arcEnd, arcClockwise,
            )) continue;

            // 去重：重根或数值噪声产生的重复交点仅保留一个
            const worldPoint = referencePlane.localToWorld(localPoint);
            const isDuplicate = validIntersections.some(
                (existing) => existing.worldPoint.distanceToSquared(worldPoint) < 1e-16,
            );
            if (isDuplicate) continue;

            validIntersections.push({ parameter: t, worldPoint });
        }

        return validIntersections;
    }

    /**
     * 求椭圆弧与椭圆弧的全部交点（仅求交，不含截取）。
     * 说明：计算两实体的全部候选交点（含各自可见区间过滤与去重），每个候选记录
     * 交点在第一实体上的位置参数（parameter）与世界坐标（worldPoint）；
     * "从全部交点中选取离延伸端最近者"由对应 intersect 方法完成。
     * @param ellipse1
     * @param ellipse2
     * @returns 全部候选交点数组（无交返回空数组）。
     */
    static computeEllipseEllipseIntersections(ellipse1: SketchEllipseObject, ellipse2: SketchEllipseObject): SketchIntersectionCandidate[] {
        const referencePlane = ellipse1.referencePlane;

        // 1. 获取两椭圆弧定义，中心投影到参考面局部 2D 坐标
        const e1Def = ellipse1.getEllipseDefinition();
        const e2Def = ellipse2.getEllipseDefinition();
        const center1Local = referencePlane.worldToLocal(e1Def.centerWorld.clone());
        const center2Local = referencePlane.worldToLocal(e2Def.centerWorld.clone());
        const { radiusX: rx1, radiusY: ry1, rotation: rot1 } = e1Def;
        const { radiusX: rx2, radiusY: ry2, rotation: rot2 } = e2Def;
        const {
            startAngle: start1,
            endAngle: end1,
            clockwise: clockwise1,
        } = e1Def;
        const {
            startAngle: start2,
            endAngle: end2,
            clockwise: clockwise2,
        } = e2Def;

        // 半轴有效性防御
        if (rx1 <= 1e-12 || ry1 <= 1e-12 || rx2 <= 1e-12 || ry2 <= 1e-12) {
            return [];
        }

        // 预计算两椭圆旋转角正余弦
        const cr1 = Math.cos(rot1);
        const sr1 = Math.sin(rot1);
        const cr2 = Math.cos(rot2);
        const sr2 = Math.sin(rot2);

        // 2. 建立交点方程：椭圆 1 参数方程代入椭圆 2 隐式方程
        //    椭圆 2 主轴系下 x'' = A1·cosθ + B1·sinθ + C1，
        //                  y'' = A2·cosθ + B2·sinθ + C2
        //    其中 ax/ay 为椭圆 1 中心相对椭圆 2 中心的偏移
        const ax = center1Local.x - center2Local.x;
        const ay = center1Local.y - center2Local.y;

        // x'' 的系数
        const A1 = rx1 * (cr1 * cr2 + sr1 * sr2);
        const B1 = ry1 * (-sr1 * cr2 + cr1 * sr2);
        const C1 = ax * cr2 + ay * sr2;
        // y'' 的系数
        const A2 = rx1 * (-cr1 * sr2 + sr1 * cr2);
        const B2 = ry1 * (sr1 * sr2 + cr1 * cr2);
        const C2 = -ax * sr2 + ay * cr2;

        // 展开 (x''²/rx2² + y''²/ry2² - 1) 的各项系数
        const P_cos2 = (A1 * A1) / (rx2 * rx2) + (A2 * A2) / (ry2 * ry2);
        const P_sin2 = (B1 * B1) / (rx2 * rx2) + (B2 * B2) / (ry2 * ry2);
        const P_cs = 2 * ((A1 * B1) / (rx2 * rx2) + (A2 * B2) / (ry2 * ry2));
        const P_cos = 2 * ((A1 * C1) / (rx2 * rx2) + (A2 * C2) / (ry2 * ry2));
        const P_sin = 2 * ((B1 * C1) / (rx2 * rx2) + (B2 * C2) / (ry2 * ry2));
        const P_const = (C1 * C1) / (rx2 * rx2) + (C2 * C2) / (ry2 * ry2) - 1;

        // 3. 万能代换 t = tan(θ/2)：cosθ = (1-t²)/(1+t²)，sinθ = 2t/(1+t²)
        //    乘 (1+t²)² 后化为四次方程 a4·t⁴ + a3·t³ + a2·t² + a1·t + a0 = 0
        const a4 = P_cos2 - P_cos + P_const;
        const a3 = 2 * (P_sin - P_cs);
        const a2 = -2 * P_cos2 + 4 * P_sin2 + 2 * P_const;
        const a1 = 2 * (P_cs + P_sin);
        const a0 = P_cos2 + P_cos + P_const;

        const tRoots = solveQuartic(a4, a3, a2, a1, a0);

        // 候选椭圆 1 参数角：θ = 2·atan(t) 覆盖 (-π, π)
        const candidateAngles: number[] = tRoots.map((t) => 2 * Math.atan(t));

        // 代换奇点检测：θ = π（t → ∞）处方程残差恰好等于 a4（cosπ = -1，sinπ = 0）
        if (Math.abs(a4) < 1e-8) {
            candidateAngles.push(Math.PI);
        }

        // 4. 过滤与去重：θ 在椭圆 1 弧区间内、交点参数角在椭圆 2 弧区间内
        //    保存交点的椭圆 1 参数角 theta（弧上参数，Step 5 用于判定第一个交点）
        const validIntersections: SketchIntersectionCandidate[] = [];
        for (const theta of candidateAngles) {
            // 椭圆 1 参数角需落在椭圆 1 弧可见区间内
            if (!isAngleInArcRange(
                theta, start1, end1, clockwise1,
            )) continue;

            // 交点局部 2D 坐标（用椭圆 1 参数方程精确计算）
            const localPoint = new Vector2(
                center1Local.x + rx1 * cr1 * Math.cos(theta) - ry1 * sr1 * Math.sin(theta),
                center1Local.y + rx1 * sr1 * Math.cos(theta) + ry1 * cr1 * Math.sin(theta),
            );

            // 变换到椭圆 2 主轴系并反解参数角，需落在椭圆 2 弧可见区间内
            const qx = localPoint.x - center2Local.x;
            const qy = localPoint.y - center2Local.y;
            const xDoublePrime = qx * cr2 + qy * sr2;
            const yDoublePrime = -qx * sr2 + qy * cr2;
            const theta2 = Math.atan2(yDoublePrime / ry2, xDoublePrime / rx2);
            if (!isAngleInArcRange(
                theta2, start2, end2, clockwise2,
            )) continue;

            // 去重：重根或数值噪声产生的重复交点仅保留一个
            const worldPoint = referencePlane.localToWorld(localPoint);
            const isDuplicate = validIntersections.some(
                (existing) => existing.worldPoint.distanceToSquared(worldPoint) < 1e-16,
            );
            if (isDuplicate) continue;

            validIntersections.push({ parameter: theta, worldPoint });
        }

        return validIntersections;
    }

    /**
     * 求椭圆弧与样条曲线的全部交点（仅求交，不含截取）。
     * 说明：计算两实体的全部候选交点（含各自可见区间过滤与去重），每个候选记录
     * 交点在第一实体上的位置参数（parameter）与世界坐标（worldPoint）；
     * "从全部交点中选取离延伸端最近者"由对应 intersect 方法完成。
     * @param ellipse
     * @param spline
     * @returns 全部候选交点数组（无交返回空数组）。
     */
    static computeEllipseSplineIntersections(ellipse: SketchEllipseObject, spline: SketchSplineObject): SketchIntersectionCandidate[] {
        const referencePlane = ellipse.referencePlane;

        // 1. 获取椭圆定义，中心投影到参考面局部 2D 坐标
        const ellipseDef = ellipse.getEllipseDefinition();
        const centerLocal = referencePlane.worldToLocal(ellipseDef.centerWorld.clone());
        const { radiusX, radiusY, rotation } = ellipseDef;
        const {
            startAngle,
            endAngle,
            clockwise,
        } = ellipseDef;

        // 半轴有效性防御
        if (radiusX <= 1e-12 || radiusY <= 1e-12) {
            return [];
        }

        // 预计算旋转角正余弦，供主轴系坐标变换复用
        const cr = Math.cos(rotation);
        const sr = Math.sin(rotation);

        // 2. 获取样条曲线采样点，构建待检查的段序列
        const curvePoints = spline.getCurvePoints();
        if (curvePoints.length < 2) {
            return [];
        }

        // 段序列：相邻采样点对；闭合样条补上首尾接缝段
        const segmentPairs: Array<[Vector3, Vector3]> = [];
        for (let i = 0; i < curvePoints.length - 1; i++) {
            segmentPairs.push([curvePoints[i]!, curvePoints[i + 1]!]);
        }
        const metadata = spline.getCurveMetadata();
        if (metadata.closureKind === 'closed') {
            segmentPairs.push([curvePoints[curvePoints.length - 1]!, curvePoints[0]!]);
        }

        // 3. 逐段与椭圆求交，记录交点沿样条的累计弧长参数
        //    有效交点列表：{ length: 弧长参数, worldPoint: 世界坐标 }
        const validIntersections: SketchIntersectionCandidate[] = [];
        // 当前段起点的累计弧长
        let accumulatedLength = 0;

        for (const [segStart, segEnd] of segmentPairs) {
            // 段端点投影到参考面局部 2D 坐标
            const a = referencePlane.worldToLocal(segStart);
            const b = referencePlane.worldToLocal(segEnd);

            const dx = b.x - a.x;
            const dy = b.y - a.y;
            const lenSq = dx * dx + dy * dy;
            const segLen = Math.sqrt(lenSq);

            // 退化段（长度 < 1e-12）：跳过，仅累计弧长
            if (lenSq < 1e-12) {
                accumulatedLength += segLen;
                continue;
            }

            // 3.1 建立一元二次方程：将段直线 P = A + λ·d 代入旋转椭圆隐式方程
            //     主轴系下 x'(λ) = a1·λ + b1，y'(λ) = a2·λ + b2，其中：
            //       a1 = dx·cosRot + dy·sinRot，b1 = u0·cosRot + v0·sinRot
            //       a2 = -dx·sinRot + dy·cosRot，b2 = -u0·sinRot + v0·cosRot
            //     展开 (a1·λ+b1)²/rx² + (a2·λ+b2)²/ry² = 1 得 a·λ² + b·λ + c = 0
            const u0 = a.x - centerLocal.x;
            const v0 = a.y - centerLocal.y;

            // x' 的 λ 系数与常数项
            const a1 = dx * cr + dy * sr;
            const b1 = u0 * cr + v0 * sr;
            // y' 的 λ 系数与常数项
            const a2 = -dx * sr + dy * cr;
            const b2 = -u0 * sr + v0 * cr;

            const aq = (a1 * a1) / (radiusX * radiusX) + (a2 * a2) / (radiusY * radiusY);
            const bq = 2 * ((a1 * b1) / (radiusX * radiusX) + (a2 * b2) / (radiusY * radiusY));
            const cq = (b1 * b1) / (radiusX * radiusX) + (b2 * b2) / (radiusY * radiusY) - 1;

            const discriminant = bq * bq - 4 * aq * cq;
            // 判别式量级参考（两项绝对值之和）：小系数几何（坐标 0.01~0.1 量级）下
            // 判别式绝对值可能远小于固定容差但符号明确，绝对容差（1e-10）会把两个
            // 不同实根误合并为重根导致漏交点，故改为相对容差判定（与 solveQuadratic 一致）
            const discriminantScale = bq * bq + Math.abs(4 * aq * cq);
            const discriminantTolerance = discriminantScale * 1e-12;

            // 判别式 < -容差 → 该段与椭圆不相交
            if (discriminant >= -discriminantTolerance) {
                const sqrtDisc = Math.sqrt(Math.max(0, discriminant));

                // 收集所有根（相切时只有 1 个，否则 2 个）
                const roots: number[] = [];
                if (discriminant <= discriminantTolerance) {
                    roots.push(-bq / (2 * aq));
                } else {
                    roots.push((-bq - sqrtDisc) / (2 * aq));
                    roots.push((-bq + sqrtDisc) / (2 * aq));
                }

                // 3.2 验证每个根：λ ∈ [0,1] 且椭圆参数角在弧区间内
                for (const lambda of roots) {
                    if (lambda < -1e-10 || lambda > 1 + 1e-10) continue;

                    const clampedLambda = Math.max(0, Math.min(1, lambda));

                    const ix = a.x + clampedLambda * dx;
                    const iy = a.y + clampedLambda * dy;

                    // 变换到椭圆主轴系并反解参数角
                    const u = ix - centerLocal.x;
                    const v = iy - centerLocal.y;
                    const xPrime = u * cr + v * sr;
                    const yPrime = -u * sr + v * cr;
                    const theta = Math.atan2(yPrime / radiusY, xPrime / radiusX);

                    if (!isAngleInArcRange(
                        theta, startAngle, endAngle, clockwise,
                    )) continue;

                    validIntersections.push({
                        parameter: accumulatedLength + clampedLambda * segLen,
                        worldPoint: referencePlane.localToWorld(new Vector2(ix, iy)),
                    });
                }
            }

            // 累计当前段弧长，供下一段交点的弧长定位
            accumulatedLength += segLen;
        }

        // 去重：相邻段共享采样点会产生重复交点（同一位置），仅保留一个
        const deduplicated: SketchIntersectionCandidate[] = [];
        for (const item of validIntersections) {
            const isDuplicate = deduplicated.some(
                (existing) => existing.worldPoint.distanceToSquared(item.worldPoint) < 1e-16,
            );
            if (!isDuplicate) {
                deduplicated.push(item);
            }
        }
        return deduplicated;
    }

    /**
     * 求椭圆弧与抛物线的全部交点（仅求交，不含截取）。
     * 说明：计算两实体的全部候选交点（含各自可见区间过滤与去重），每个候选记录
     * 交点在第一实体上的位置参数（parameter）与世界坐标（worldPoint）；
     * "从全部交点中选取离延伸端最近者"由对应 intersect 方法完成。
     * @param ellipse
     * @param parabola
     * @returns 全部候选交点数组（无交返回空数组）。
     */
    static computeEllipseParabolaIntersections(ellipse: SketchEllipseObject, parabola: SketchParabolaObject): SketchIntersectionCandidate[] {
        const referencePlane = ellipse.referencePlane;

        // 1. 获取椭圆定义，中心投影到参考面局部 2D 坐标
        const ellipseDef = ellipse.getEllipseDefinition();
        const centerLocal = referencePlane.worldToLocal(ellipseDef.centerWorld.clone());
        const { radiusX, radiusY, rotation } = ellipseDef;
        const {
            startAngle,
            endAngle,
            clockwise,
        } = ellipseDef;

        // 半轴有效性防御
        if (radiusX <= 1e-12 || radiusY <= 1e-12) {
            return [];
        }

        // 预计算椭圆旋转角正余弦，供主轴系坐标变换复用
        const cr = Math.cos(rotation);
        const sr = Math.sin(rotation);

        // 2. 由抛物线定义推导局部框架（复刻 SketchParabolaObject.resolveFrameFromDefinition）
        const definition = parabola.getParabolaDefinition();
        const apexWorld = definition.apexWorld.clone();
        const focalWorld = definition.focalWorld.clone();
        const { startParameter, endParameter } = definition;

        // 参数区间边界（兼容端点反向越过：按最小/最大归一）
        const minParam = Math.min(startParameter, endParameter);
        const maxParam = Math.max(startParameter, endParameter);

        // 顶点指向焦点的轴向（去掉参考面法向分量，保证落在参考面内）
        const normal = referencePlane.normal.clone().normalize();
        const axisVector = focalWorld.clone().sub(apexWorld);
        axisVector.sub(normal.clone().multiplyScalar(axisVector.dot(normal)));
        const focalLength = axisVector.length();

        // 焦点与顶点重合（投影退化）：抛物线定义无效，无交点
        if (focalLength < 1e-12) {
            return [];
        }

        const axisDirWorld = axisVector.normalize();
        const sideDirWorld = new Vector3().crossVectors(axisDirWorld, normal).normalize();

        // 将轴向/侧向轴投影到参考面局部 2D（点差法，与 SketchParabolaObject 内部一致）
        const apexLocal = referencePlane.worldToLocal(apexWorld);
        const axis2D = referencePlane.worldToLocal(apexWorld.clone().add(axisDirWorld)).sub(apexLocal);
        const side2D = referencePlane.worldToLocal(apexWorld.clone().add(sideDirWorld)).sub(apexLocal);

        // 3. 建立四次方程：将 P(t) 代入椭圆隐式方程
        //    椭圆主轴系下 x' = A1·t² + B1·t + C1，y' = A2·t² + B2·t + C2，其中：
        //      A1 = (axis·cr + axis·sr)/(4f)，B1 = side·cr + side·sr，C1 = u·cr + v·sr
        //      A2 = (-axis·sr + axis·cr)/(4f)，B2 = -side·sr + side·cr，C2 = -u·sr + v·cr
        //    展开 (A1·t²+B1·t+C1)²/rx² + (A2·t²+B2·t+C2)²/ry² = 1
        //    得 a4·t⁴ + a3·t³ + a2·t² + a1·t + a0 = 0（u = apex - 椭圆中心）
        const ux = apexLocal.x - centerLocal.x;
        const uy = apexLocal.y - centerLocal.y;

        // x' 的 t²/t¹/t⁰ 系数
        const A1 = (axis2D.x * cr + axis2D.y * sr) / (4 * focalLength);
        const B1 = side2D.x * cr + side2D.y * sr;
        const C1 = ux * cr + uy * sr;
        // y' 的 t²/t¹/t⁰ 系数
        const A2 = (-axis2D.x * sr + axis2D.y * cr) / (4 * focalLength);
        const B2 = -side2D.x * sr + side2D.y * cr;
        const C2 = -ux * sr + uy * cr;

        const a4 = (A1 * A1) / (radiusX * radiusX) + (A2 * A2) / (radiusY * radiusY);
        const a3 = 2 * ((A1 * B1) / (radiusX * radiusX) + (A2 * B2) / (radiusY * radiusY));
        const a2 = (2 * A1 * C1 + B1 * B1) / (radiusX * radiusX)
            + (2 * A2 * C2 + B2 * B2) / (radiusY * radiusY);
        const a1 = 2 * ((B1 * C1) / (radiusX * radiusX) + (B2 * C2) / (radiusY * radiusY));
        const a0 = (C1 * C1) / (radiusX * radiusX) + (C2 * C2) / (radiusY * radiusY) - 1;

        const tRoots = solveQuartic(a4, a3, a2, a1, a0);

        // 4. 过滤与去重：t 在抛物线参数区间内、交点参数角在椭圆弧区间内
        //    保存交点的抛物线参数 t（位置参数，Step 5 用于判定第一个交点）
        const validIntersections: SketchIntersectionCandidate[] = [];
        for (const t of tRoots) {
            // 抛物线参数需落在可见参数区间内
            if (t < minParam - 1e-10 || t > maxParam + 1e-10) continue;

            // 抛物线上的交点（局部 2D）
            const localPoint = new Vector2(
                apexLocal.x + side2D.x * t + axis2D.x * (t * t) / (4 * focalLength),
                apexLocal.y + side2D.y * t + axis2D.y * (t * t) / (4 * focalLength),
            );

            // 变换到椭圆主轴系并反解参数角，需落在椭圆弧可见区间内
            const qx = localPoint.x - centerLocal.x;
            const qy = localPoint.y - centerLocal.y;
            const xPrime = qx * cr + qy * sr;
            const yPrime = -qx * sr + qy * cr;
            const theta = Math.atan2(yPrime / radiusY, xPrime / radiusX);
            if (!isAngleInArcRange(
                theta, startAngle, endAngle, clockwise,
            )) continue;

            // 去重：重根或数值噪声产生的重复交点仅保留一个
            const worldPoint = referencePlane.localToWorld(localPoint);
            const isDuplicate = validIntersections.some(
                (existing) => existing.worldPoint.distanceToSquared(worldPoint) < 1e-16,
            );
            if (isDuplicate) continue;

            validIntersections.push({ parameter: t, worldPoint });
        }

        return validIntersections;
    }

    /**
     * 求样条曲线与样条曲线的全部交点（仅求交，不含截取）。
     * 说明：计算两实体的全部候选交点（含各自可见区间过滤与去重），每个候选记录
     * 交点在第一实体上的位置参数（parameter）与世界坐标（worldPoint）；
     * "从全部交点中选取离延伸端最近者"由对应 intersect 方法完成。
     * @param spline1
     * @param spline2
     * @returns 全部候选交点数组（无交返回空数组）。
     */
    static computeSplineSplineIntersections(spline1: SketchSplineObject, spline2: SketchSplineObject): SketchIntersectionCandidate[] {
        const referencePlane = spline1.referencePlane;

        // 1. 构建两条样条的段序列（相邻采样点对；闭合样条补首尾接缝段）
        const buildSegmentPairs = (spline: SketchSplineObject): Array<[Vector3, Vector3]> => {
            const curvePoints = spline.getCurvePoints();
            if (curvePoints.length < 2) {
                return [];
            }
            const pairs: Array<[Vector3, Vector3]> = [];
            for (let i = 0; i < curvePoints.length - 1; i++) {
                pairs.push([curvePoints[i]!, curvePoints[i + 1]!]);
            }
            if (spline.getCurveMetadata().closureKind === 'closed') {
                pairs.push([curvePoints[curvePoints.length - 1]!, curvePoints[0]!]);
            }
            return pairs;
        };

        const segmentPairs1 = buildSegmentPairs(spline1);
        const segmentPairs2 = buildSegmentPairs(spline2);
        if (segmentPairs1.length === 0 || segmentPairs2.length === 0) {
            return [];
        }

        // spline1 采样点（用于延伸侧判定与弧长累计）
        const curvePoints1 = spline1.getCurvePoints();

        // 2. 逐对段求交，记录交点沿 spline1 的累计弧长参数
        //    有效交点列表：{ length: spline1 弧长参数, worldPoint: 世界坐标 }
        const validIntersections: SketchIntersectionCandidate[] = [];
        // 当前 spline1 段起点的累计弧长
        let accumulatedLength = 0;

        for (const [seg1Start, seg1End] of segmentPairs1) {
            // spline1 段端点投影到参考面局部 2D 坐标
            const a1 = referencePlane.worldToLocal(seg1Start);
            const b1 = referencePlane.worldToLocal(seg1End);
            const d1x = b1.x - a1.x;
            const d1y = b1.y - a1.y;
            const len1Sq = d1x * d1x + d1y * d1y;
            const seg1Len = Math.sqrt(len1Sq);

            // 退化段（长度 < 1e-12）：跳过，仅累计弧长
            if (len1Sq < 1e-12) {
                accumulatedLength += seg1Len;
                continue;
            }

            for (const [seg2Start, seg2End] of segmentPairs2) {
                // spline2 段端点投影到参考面局部 2D 坐标
                const a2 = referencePlane.worldToLocal(seg2Start);
                const b2 = referencePlane.worldToLocal(seg2End);
                const d2x = b2.x - a2.x;
                const d2y = b2.y - a2.y;

                // 两段所在直线平行：该段对无交点
                const denom = d1x * d2y - d1y * d2x;
                if (Math.abs(denom) < 1e-12) continue;

                const dx = a2.x - a1.x;
                const dy = a2.y - a1.y;

                // 段参数 t（spline1 段）与 s（spline2 段），2D 克莱姆法则
                const t = (dx * d2y - dy * d2x) / denom;
                const s = (dx * d1y - dy * d1x) / denom;

                // 交点必须同时落在两段范围内
                if (t < -1e-10 || t > 1 + 1e-10) continue;
                if (s < -1e-10 || s > 1 + 1e-10) continue;

                const clampedT = Math.max(0, Math.min(1, t));
                const ix = a1.x + clampedT * d1x;
                const iy = a1.y + clampedT * d1y;

                validIntersections.push({
                    parameter: accumulatedLength + clampedT * seg1Len,
                    worldPoint: referencePlane.localToWorld(new Vector2(ix, iy)),
                });
            }

            // 累计当前段弧长，供下一段交点的弧长定位
            accumulatedLength += seg1Len;
        }

        // 去重：相邻段共享采样点会产生重复交点（同一位置），仅保留一个
        const deduplicated: SketchIntersectionCandidate[] = [];
        for (const item of validIntersections) {
            const isDuplicate = deduplicated.some(
                (existing) => existing.worldPoint.distanceToSquared(item.worldPoint) < 1e-16,
            );
            if (!isDuplicate) {
                deduplicated.push(item);
            }
        }
        return deduplicated;
    }

    /**
     * 求样条曲线与抛物线的全部交点（仅求交，不含截取）。
     * 说明：计算两实体的全部候选交点（含各自可见区间过滤与去重），每个候选记录
     * 交点在第一实体上的位置参数（parameter）与世界坐标（worldPoint）；
     * "从全部交点中选取离延伸端最近者"由对应 intersect 方法完成。
     * @param spline
     * @param parabola
     * @returns 全部候选交点数组（无交返回空数组）。
     */
    static computeSplineParabolaIntersections(spline: SketchSplineObject, parabola: SketchParabolaObject): SketchIntersectionCandidate[] {
        const referencePlane = spline.referencePlane;

        // 1. 由抛物线定义推导局部框架（复刻 SketchParabolaObject.resolveFrameFromDefinition）
        const definition = parabola.getParabolaDefinition();
        const apexWorld = definition.apexWorld.clone();
        const focalWorld = definition.focalWorld.clone();
        const { startParameter, endParameter } = definition;

        // 参数区间边界（兼容端点反向越过：按最小/最大归一）
        const minParam = Math.min(startParameter, endParameter);
        const maxParam = Math.max(startParameter, endParameter);

        // 顶点指向焦点的轴向（去掉参考面法向分量，保证落在参考面内）
        const normal = referencePlane.normal.clone().normalize();
        const axisVector = focalWorld.clone().sub(apexWorld);
        axisVector.sub(normal.clone().multiplyScalar(axisVector.dot(normal)));
        const focalLength = axisVector.length();

        // 焦点与顶点重合（投影退化）：抛物线定义无效，无交点
        if (focalLength < 1e-12) {
            return [];
        }

        const axisDirWorld = axisVector.normalize();
        const sideDirWorld = new Vector3().crossVectors(axisDirWorld, normal).normalize();

        // 将轴向/侧向轴投影到参考面局部 2D（点差法，与 SketchParabolaObject 内部一致）
        const apexLocal = referencePlane.worldToLocal(apexWorld);
        const axis2D = referencePlane.worldToLocal(apexWorld.clone().add(axisDirWorld)).sub(apexLocal);
        const side2D = referencePlane.worldToLocal(apexWorld.clone().add(sideDirWorld)).sub(apexLocal);
        const axd = axis2D.x;
        const ayd = axis2D.y;
        const sxd = side2D.x;
        const syd = side2D.y;

        // 2. 获取样条曲线采样点，构建待检查的段序列
        const curvePoints = spline.getCurvePoints();
        if (curvePoints.length < 2) {
            return [];
        }

        // 段序列：相邻采样点对；闭合样条补上首尾接缝段
        const segmentPairs: Array<[Vector3, Vector3]> = [];
        for (let i = 0; i < curvePoints.length - 1; i++) {
            segmentPairs.push([curvePoints[i]!, curvePoints[i + 1]!]);
        }
        const metadata = spline.getCurveMetadata();
        if (metadata.closureKind === 'closed') {
            segmentPairs.push([curvePoints[curvePoints.length - 1]!, curvePoints[0]!]);
        }

        // 3. 逐段与抛物线求交，记录交点的抛物线参数
        //    有效交点列表：{ t: 抛物线参数, worldPoint: 世界坐标 }
        const validIntersections: SketchIntersectionCandidate[] = [];
        for (const [segStart, segEnd] of segmentPairs) {
            // 段端点投影到参考面局部 2D 坐标
            const segA = referencePlane.worldToLocal(segStart);
            const segB = referencePlane.worldToLocal(segEnd);

            const dx = segB.x - segA.x;
            const dy = segB.y - segA.y;
            const lenSq = dx * dx + dy * dy;

            // 退化段（长度 < 1e-12）：跳过
            if (lenSq < 1e-12) {
                continue;
            }

            // 3.1 建立一元二次方程：将段直线 P = A + λ·d 代入抛物线共线条件
            //     cross(P(t) - A, d) = 0，展开得 A·t² + B·t + C = 0，其中：
            //       A = (axd·dy - ayd·dx) / (4f)
            //       B = sxd·dy - syd·dx
            //       C = (ax - Ax)·dy - (ay - Ay)·dx
            const Aq = (axd * dy - ayd * dx) / (4 * focalLength);
            const Bq = sxd * dy - syd * dx;
            const Cq = (apexLocal.x - segA.x) * dy - (apexLocal.y - segA.y) * dx;

            // 收集候选参数根
            const roots: number[] = [];
            if (Math.abs(Aq) < 1e-12) {
                // 退化：段与抛物线轴向平行，退化为一次方程 B·t + C = 0
                if (Math.abs(Bq) < 1e-12) {
                    continue; // 段与抛物线渐近方向平行且不共线，无交点
                }
                roots.push(-Cq / Bq);
            } else {
                const discriminant = Bq * Bq - 4 * Aq * Cq;
                // 判别式量级参考（两项绝对值之和）：小系数几何（坐标 0.01~0.1 量级）下
                // 判别式绝对值可能远小于固定容差但符号明确，绝对容差（1e-10）会把两个
                // 不同实根误合并为重根导致漏交点，故改为相对容差判定（与 solveQuadratic 一致）
                const discriminantScale = Bq * Bq + Math.abs(4 * Aq * Cq);
                const discriminantTolerance = discriminantScale * 1e-12;

                // 判别式 < -容差 → 无实根（段与抛物线不相交）
                if (discriminant < -discriminantTolerance) {
                    continue;
                }
                const sqrtDisc = Math.sqrt(Math.max(0, discriminant));

                if (discriminant <= discriminantTolerance) {
                    roots.push(-Bq / (2 * Aq));
                } else {
                    roots.push((-Bq - sqrtDisc) / (2 * Aq));
                    roots.push((-Bq + sqrtDisc) / (2 * Aq));
                }
            }

            // 3.2 验证每个根：t 在参数区间内且段参数 λ ∈ [0,1]
            for (const t of roots) {
                if (t < minParam - 1e-10 || t > maxParam + 1e-10) continue;

                // 抛物线上的交点（局部 2D，用抛物线参数式精确计算）
                const curveLocal = new Vector2(
                    apexLocal.x + sxd * t + axd * (t * t) / (4 * focalLength),
                    apexLocal.y + syd * t + ayd * (t * t) / (4 * focalLength),
                );

                // 段参数 λ = ((P - A)·d) / |d|²，需落在 [0,1] 内
                const lambda = ((curveLocal.x - segA.x) * dx + (curveLocal.y - segA.y) * dy) / lenSq;
                if (lambda < -1e-10 || lambda > 1 + 1e-10) continue;

                validIntersections.push({
                    parameter: t,
                    worldPoint: referencePlane.localToWorld(curveLocal),
                });
            }
        }

        // 去重：相邻段共享采样点会产生重复交点（同一位置），仅保留一个
        const deduplicated: SketchIntersectionCandidate[] = [];
        for (const item of validIntersections) {
            const isDuplicate = deduplicated.some(
                (existing) => existing.worldPoint.distanceToSquared(item.worldPoint) < 1e-16,
            );
            if (!isDuplicate) {
                deduplicated.push(item);
            }
        }
        return deduplicated;
    }

    /**
     * 求抛物线与抛物线的全部交点（仅求交，不含截取）。
     * 说明：计算两实体的全部候选交点（含各自可见区间过滤与去重），每个候选记录
     * 交点在第一实体上的位置参数（parameter）与世界坐标（worldPoint）；
     * "从全部交点中选取离延伸端最近者"由对应 intersect 方法完成。
     * @param parabola1
     * @param parabola2
     * @returns 全部候选交点数组（无交返回空数组）。
     */
    static computeParabolaParabolaIntersections(parabola1: SketchParabolaObject, parabola2: SketchParabolaObject): SketchIntersectionCandidate[] {
        const referencePlane = parabola1.referencePlane;

        // 1. 由两条抛物线定义推导局部框架（复刻 SketchParabolaObject.resolveFrameFromDefinition）
        const definition1 = parabola1.getParabolaDefinition();
        const definition2 = parabola2.getParabolaDefinition();

        // 抛物线 1：顶点、轴向、侧向轴、焦距
        const apex1World = definition1.apexWorld.clone();
        const focal1World = definition1.focalWorld.clone();
        const { startParameter: start1, endParameter: end1 } = definition1;

        // 抛物线 2：顶点、轴向、侧向轴、焦距与参数区间
        const apex2World = definition2.apexWorld.clone();
        const focal2World = definition2.focalWorld.clone();
        const { startParameter: start2, endParameter: end2 } = definition2;

        // 参数区间边界（兼容端点反向越过：按最小/最大归一）
        const minParam1 = Math.min(start1, end1);
        const maxParam1 = Math.max(start1, end1);
        const minParam2 = Math.min(start2, end2);
        const maxParam2 = Math.max(start2, end2);

        // 参考面法向（用于把焦点-顶点向量投影到参考面内）
        const normal = referencePlane.normal.clone().normalize();

        // 推导单条抛物线的局部框架；焦点与顶点重合（投影退化）时返回 null
        const resolveFrame = (
            apexWorld: Vector3,
            focalWorld: Vector3,
        ): { apexLocal: Vector2; axis2D: Vector2; side2D: Vector2; focalLength: number } | null => {
            const axisVector = focalWorld.clone().sub(apexWorld);
            axisVector.sub(normal.clone().multiplyScalar(axisVector.dot(normal)));
            const focalLength = axisVector.length();
            if (focalLength < 1e-12) {
                return null;
            }
            const axisDirWorld = axisVector.normalize();
            const sideDirWorld = new Vector3().crossVectors(axisDirWorld, normal).normalize();
            const apexLocal = referencePlane.worldToLocal(apexWorld);
            const axis2D = referencePlane.worldToLocal(apexWorld.clone().add(axisDirWorld)).sub(apexLocal);
            const side2D = referencePlane.worldToLocal(apexWorld.clone().add(sideDirWorld)).sub(apexLocal);
            return { apexLocal, axis2D, side2D, focalLength };
        };

        const frame1 = resolveFrame(apex1World, focal1World);
        const frame2 = resolveFrame(apex2World, focal2World);
        if (!frame1 || !frame2) {
            return []; // 任一定义无效（焦点与顶点重合）
        }

        // 2. 建立四次方程：将抛物线 2 参数方程变换到抛物线 1 局部系并代入隐式方程
        //    抛物线 1 局部系：原点 = apex1，x 轴 = side1（侧向轴），y 轴 = axis1（轴向），
        //    该系下抛物线 1 为标准形 y = x²/(4f1)，隐式方程 x² - 4f1·y = 0；
        //    P2(u) 在该系下 x1(u) = X0 + X1·u + X2·u²，y1(u) = Y0 + Y1·u + Y2·u²
        const deltaX = frame2.apexLocal.x - frame1.apexLocal.x;
        const deltaY = frame2.apexLocal.y - frame1.apexLocal.y;

        // x1(u) 系数（抛物线 2 侧向轴/轴向投影到抛物线 1 侧向轴）
        const X0 = deltaX * frame1.side2D.x + deltaY * frame1.side2D.y;
        const X1 = frame2.side2D.x * frame1.side2D.x + frame2.side2D.y * frame1.side2D.y;
        const X2 = (frame2.axis2D.x * frame1.side2D.x + frame2.axis2D.y * frame1.side2D.y)
            / (4 * frame2.focalLength);
        // y1(u) 系数（抛物线 2 侧向轴/轴向投影到抛物线 1 轴向）
        const Y0 = deltaX * frame1.axis2D.x + deltaY * frame1.axis2D.y;
        const Y1 = frame2.side2D.x * frame1.axis2D.x + frame2.side2D.y * frame1.axis2D.y;
        const Y2 = (frame2.axis2D.x * frame1.axis2D.x + frame2.axis2D.y * frame1.axis2D.y)
            / (4 * frame2.focalLength);

        // 展开 (X0 + X1·u + X2·u²)² - 4f1·(Y0 + Y1·u + Y2·u²) = 0，
        // 得 a4·u⁴ + a3·u³ + a2·u² + a1·u + a0 = 0
        const f1 = frame1.focalLength;
        const a4 = X2 * X2;
        const a3 = 2 * X1 * X2;
        const a2 = X1 * X1 + 2 * X0 * X2 - 4 * f1 * Y2;
        const a1 = 2 * X0 * X1 - 4 * f1 * Y1;
        const a0 = X0 * X0 - 4 * f1 * Y0;

        const uRoots = solveQuartic(a4, a3, a2, a1, a0);

        // 3. 过滤与去重：u 在抛物线 2 参数区间内、交点侧向参数在抛物线 1 参数区间内
        //    保存交点在抛物线 1 上的侧向参数 t1（位置参数，Step 4 用于判定第一个交点）
        const validIntersections: SketchIntersectionCandidate[] = [];
        for (const u of uRoots) {
            // 抛物线 2 参数需落在可见参数区间内
            if (u < minParam2 - 1e-10 || u > maxParam2 + 1e-10) continue;

            // 抛物线 2 上的交点（局部 2D，用抛物线 2 参数式精确计算）
            const pointOn2 = new Vector2(
                frame2.apexLocal.x + frame2.side2D.x * u + frame2.axis2D.x * (u * u) / (4 * frame2.focalLength),
                frame2.apexLocal.y + frame2.side2D.y * u + frame2.axis2D.y * (u * u) / (4 * frame2.focalLength),
            );

            // 交点相对抛物线 1 顶点的侧向参数 t1 = (P - A1)·side1，需落在抛物线 1 可见参数区间内
            const t1 = (pointOn2.x - frame1.apexLocal.x) * frame1.side2D.x
                + (pointOn2.y - frame1.apexLocal.y) * frame1.side2D.y;
            if (t1 < minParam1 - 1e-10 || t1 > maxParam1 + 1e-10) continue;

            // 去重：重根或数值噪声产生的重复交点仅保留一个
            const worldPoint = referencePlane.localToWorld(pointOn2);
            const isDuplicate = validIntersections.some(
                (existing) => existing.worldPoint.distanceToSquared(worldPoint) < 1e-16,
            );
            if (isDuplicate) continue;

            validIntersections.push({ parameter: t1, worldPoint });
        }

        return validIntersections;
    }

    /**
     * 求两个任意类型草图实体的全部交点（统一分发入口）。
     * 说明：
     * 1. 按实体类型组合分发到对应 compute*Intersections（仅直线/圆弧/椭圆弧/样条/抛物线参与求交）；
     * 2. 样式样条（SketchStyleSplineObject）与普通样条同等参与样条求交；
     * 3. 槽口/集合等复合实体或未支持组合返回空数组；
     * 4. 返回全部候选交点（含端点重合交点），端点豁免由调用方按业务语义处理。
     *
     * @param entityA 第一个草图实体。
     * @param entityB 第二个草图实体。
     * @returns 全部候选交点数组（无交或类型不受支持返回空数组）。
     */
    static computeEntityIntersections(
        entityA: OffsetCandidate,
        entityB: OffsetCandidate,
    ): SketchIntersectionCandidate[] {
        const a = entityA;
        const b = entityB;

        // 线-线
        if (a instanceof SketchLineObject && b instanceof SketchLineObject) {
            return SketchEntityIntersect.computeLineLineIntersections(a, b);
        }
        // 线-弧
        if (a instanceof SketchLineObject && b instanceof SketchArcObject) {
            return SketchEntityIntersect.computeLineArcIntersections(a, b);
        }
        // 线-椭圆
        if (a instanceof SketchLineObject && b instanceof SketchEllipseObject) {
            return SketchEntityIntersect.computeLineEllipseIntersections(a, b);
        }
        // 线-样条（含样式样条）
        if (a instanceof SketchLineObject && SketchEntityIntersect.isSplineLike(b)) {
            return SketchEntityIntersect.computeLineSplineIntersections(a, SketchEntityIntersect.asSpline(b));
        }
        // 线-抛物线
        if (a instanceof SketchLineObject && b instanceof SketchParabolaObject) {
            return SketchEntityIntersect.computeLineParabolaIntersections(a, b);
        }
        // 弧-弧
        if (a instanceof SketchArcObject && b instanceof SketchArcObject) {
            return SketchEntityIntersect.computeArcArcIntersections(a, b);
        }
        // 弧-椭圆
        if (a instanceof SketchArcObject && b instanceof SketchEllipseObject) {
            return SketchEntityIntersect.computeArcEllipseIntersections(a, b);
        }
        // 弧-抛物线
        if (a instanceof SketchArcObject && b instanceof SketchParabolaObject) {
            return SketchEntityIntersect.computeArcParabolaIntersections(a, b);
        }
        // 弧-样条
        if (a instanceof SketchArcObject && SketchEntityIntersect.isSplineLike(b)) {
            return SketchEntityIntersect.computeArcSplineIntersections(a, SketchEntityIntersect.asSpline(b));
        }
        // 椭圆-椭圆
        if (a instanceof SketchEllipseObject && b instanceof SketchEllipseObject) {
            return SketchEntityIntersect.computeEllipseEllipseIntersections(a, b);
        }
        // 椭圆-样条
        if (a instanceof SketchEllipseObject && SketchEntityIntersect.isSplineLike(b)) {
            return SketchEntityIntersect.computeEllipseSplineIntersections(a, SketchEntityIntersect.asSpline(b));
        }
        // 椭圆-抛物线
        if (a instanceof SketchEllipseObject && b instanceof SketchParabolaObject) {
            return SketchEntityIntersect.computeEllipseParabolaIntersections(a, b);
        }
        // 样条-样条
        if (SketchEntityIntersect.isSplineLike(a) && SketchEntityIntersect.isSplineLike(b)) {
            return SketchEntityIntersect.computeSplineSplineIntersections(
                SketchEntityIntersect.asSpline(a), SketchEntityIntersect.asSpline(b),
            );
        }
        // 样条-抛物线
        if (SketchEntityIntersect.isSplineLike(a) && b instanceof SketchParabolaObject) {
            return SketchEntityIntersect.computeSplineParabolaIntersections(SketchEntityIntersect.asSpline(a), b);
        }
        // 抛物线-抛物线
        if (a instanceof SketchParabolaObject && b instanceof SketchParabolaObject) {
            return SketchEntityIntersect.computeParabolaParabolaIntersections(a, b);
        }
        // —— 逆序组合（a/b 类型次序与正序函数不一致）：调用正序函数并交换参数 ——
        // 椭圆-线
        if (a instanceof SketchEllipseObject && b instanceof SketchLineObject) {
            return SketchEntityIntersect.computeLineEllipseIntersections(b, a);
        }
        // 样条-线
        if (SketchEntityIntersect.isSplineLike(a) && b instanceof SketchLineObject) {
            return SketchEntityIntersect.computeLineSplineIntersections(b, SketchEntityIntersect.asSpline(a));
        }
        // 抛物线-线
        if (a instanceof SketchParabolaObject && b instanceof SketchLineObject) {
            return SketchEntityIntersect.computeLineParabolaIntersections(b, a);
        }
        // 椭圆-弧
        if (a instanceof SketchEllipseObject && b instanceof SketchArcObject) {
            return SketchEntityIntersect.computeArcEllipseIntersections(b, a);
        }
        // 抛物线-弧
        if (a instanceof SketchParabolaObject && b instanceof SketchArcObject) {
            return SketchEntityIntersect.computeArcParabolaIntersections(b, a);
        }
        // 样条-弧
        if (SketchEntityIntersect.isSplineLike(a) && b instanceof SketchArcObject) {
            return SketchEntityIntersect.computeArcSplineIntersections(b, SketchEntityIntersect.asSpline(a));
        }
        // 样条-椭圆
        if (SketchEntityIntersect.isSplineLike(a) && b instanceof SketchEllipseObject) {
            return SketchEntityIntersect.computeEllipseSplineIntersections(b, SketchEntityIntersect.asSpline(a));
        }
        // 抛物线-椭圆
        if (a instanceof SketchParabolaObject && b instanceof SketchEllipseObject) {
            return SketchEntityIntersect.computeEllipseParabolaIntersections(b, a);
        }
        // 抛物线-样条
        if (a instanceof SketchParabolaObject && SketchEntityIntersect.isSplineLike(b)) {
            return SketchEntityIntersect.computeSplineParabolaIntersections(SketchEntityIntersect.asSpline(b), a);
        }
        // 槽口/集合等复合实体或未支持组合：无交点
        return [];
    }

    /**
     * 判断实体是否可按"样条"参与求交（样条对象或样式样条对象）。
     * @param entity 待判断实体。
     * @returns 样条/样式样条返回 true。
     */
    private static isSplineLike(entity: OffsetCandidate): boolean {
        return entity instanceof SketchSplineObject || entity instanceof SketchStyleSplineObject;
    }

    /**
     * 将实体归一为样条输入（样式样条与普通样条共享 getCurvePoints/getCurveMetadata 协议）。
     * @param entity 待归一实体。
     * @returns 样条对象（样式样条按协议视同样条）。
     */
    private static asSpline(entity: OffsetCandidate): SketchSplineObject {
        return entity as unknown as SketchSplineObject;
    }
}
